import "./email-modal.scss";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { CrossIcon } from "../../../../../assets/svg";
import { P2Alert, P2Button, P2TextInput } from "react-bootstrap-common-script";
import { SharedValidators } from "../../../../../utils/validators/shared/validators";
import { useAppSelector } from "../../../../../redux/hook";
import { ModalBackground } from "../../../shared/modal-background";
import { AlternativeEmailDto } from "../../../../../models/polymer/AlternativeEmailDto.model";
import { PolymerAxios } from "../../../../../utils/axios";
import { API_Solicitor_SaveAlternativeEmail } from "../../../../../utils/api";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";

interface EmailModalHandle {
  getIsShowing: () => boolean;
  setIsShowing: (value: boolean) => void;
}

const EmailModal = forwardRef<EmailModalHandle, {}>(({}, ref) => {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  // States
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);

  const { solicitorCaseSummaryData, solcitorCaseSummaryRefetch } = useSolicitorCaseDetailsContext();

  const [isShowing, setIsShowing] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>(solicitorCaseSummaryData?.alternative_email_address ?? "");
  const [isEmailApiError, setIsEmailApiError] = useState<boolean>(false);

  // Refs
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Derived values
  const { isValid, message } = SharedValidators.Email(newEmail);

  // Functions
  function cancel() {
    setNewEmail(solicitorCaseSummaryData?.alternative_email_address ?? "");
    setIsEmailApiError(false);
    setIsShowing(false);
  }

  const postNewEmail = async () => {
    let payload: AlternativeEmailDto = {
      application_uuid: applicationUuid ?? "",
      email: newEmail,
    };

    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_SaveAlternativeEmail}`, payload);

      if (response.status === 200) {
        solcitorCaseSummaryRefetch();
        setIsShowing(false);
        setIsEmailApiError(false);
      } else {
        setIsEmailApiError(true);
      }
    } catch (e) {
      setIsEmailApiError(true);
    }
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        getIsShowing: () => isShowing,
        setIsShowing: (value: boolean) => {
          setIsShowing(value);
        },
      };
    },
    [isShowing]
  );

  // Effects
  useEffect(() => {
    if (solicitorCaseSummaryData?.alternative_email_address && !newEmail) setNewEmail(solicitorCaseSummaryData.alternative_email_address);
  }, [solicitorCaseSummaryData]);

  return isShowing ? (
    <ModalBackground innerComponentRef={modalContainerRef} callback={cancel}>
      <div ref={modalContainerRef} className="polymer-email-modal__container">
        <div className="polymer-email-modal__header-container">
          <h2 className="polymer-email-modal__title">Add alternate email address</h2>
          <button onClick={cancel}>
            <CrossIcon fillColor="#0C8188" />
          </button>
        </div>

        <p className="polymer-email-modal__text">
          You may add an alternate email address to receive notifications from HDB regarding this specific case. Otherwise, notifications will be sent to the
          email address provided by the applicant during the application stage.
        </p>

        <div style={{ marginBottom: 24 }}>
          <P2TextInput
            placeholder="Please type your email address"
            value={newEmail}
            isError={!isValid}
            errorMsg={message}
            required
            maxLength={100}
            onChange={(value: string) => {
              setNewEmail(value);
              setIsEmailApiError(false);
            }}
          />
        </div>

        {isEmailApiError ? (
          <div style={{ marginBottom: 24 }}>
            <P2Alert variant="danger" withBorder={true} text="An error occurred while saving. Please try again." />
          </div>
        ) : null}

        <div className="polymer-email-modal__button-container">
          <P2Button onClick={cancel} variant="outline-primary">
            Cancel
          </P2Button>
          <P2Button disabled={!isValid} onClick={postNewEmail} variant="primary-1">
            Save
          </P2Button>
        </div>
      </div>
    </ModalBackground>
  ) : null;
});

export { EmailModal, type EmailModalHandle };
